import {
  CompositeDecorator,
  ContentState,
  EditorState,
  Modifier,
  RichUtils,
  SelectionState,
  convertToRaw,
} from 'draft-js';
import VariableDecorator from '../ContextualMenu/VariableDecorator';
import ImageDecorator from '../common/ImageDecorator';
import { ItemTypes, MENU_ANCESTORS } from '../../constants/constants';
import emojiToolkit from 'emoji-toolkit';
import i18next from 'i18next';
import DraftEmoji from '../common/DraftEmoji';
import { NativeTypes } from 'react-dnd-html5-backend';
import { useStore } from '../../store/store';
import { applyVariableLineStyle } from '../../utils/variableLineStyle';

export const handleAlignmentChange = (boxId, updateBox, selectedAlignment) => {
  updateBox(boxId, (box) => {
    box.content.alignment = selectedAlignment;
  });
};

export const handleImagePositionInText = (
  boxId,
  updateBox,
  selectedPosition
) => {
  updateBox(boxId, (box) => {
    box.content.imagePosition = selectedPosition;
  });
};

const sides = ['left', 'top', 'bottom', 'right'];

export const hasAllBordersOn = (box) => {
  return sides.every((side) => {
    return getBoxBorder(box, side);
  });
};

const mutateBoxAllBorders = (box, value) => {
  sides.forEach((side) => {
    mutateBoxBorder(box, side, value);
  });
};

export const getBoxBorder = (box, side) => {
  if (box.type === 'shape') {
    return box.borders;
  } else {
    return box.border && box.border[side];
  }
};
export const getBoxBorderColor = (box) => {
  if (box.type === 'shape') {
    return box.borders && box.borders.color;
  } else {
    return box.border && box.border.color;
  }
};

const mutateToggleBoxBorder = (box, side) => {
  if (box.type === 'shape') {
    if (!box.borders) {
      box.borders = {};
    }
    box.borders = !box.borders;
  } else {
    if (!box.border) {
      box.border = {};
    }
    box.border.size = 3;
    box.border[side] = !box.border[side];
  }
};

const mutateBoxBorder = (box, side, value) => {
  if (box.type === 'shape') {
    if (!box.borders) {
      box.borders = {};
    }
    box.borders[side] = value;
  } else {
    if (!box.border) {
      box.border = {};
    }
    box.border.size = 3;
    box.border[side] = value;
  }
};

export const handleBorderChangement = (boxId, updateBox, sides) => {
  updateBox(boxId, (box) => {
    if (sides.length === 4) {
      if (hasAllBordersOn(box)) {
        mutateBoxAllBorders(box, false);
      } else {
        mutateBoxAllBorders(box, true);
      }
    } else {
      sides.forEach((side) => {
        mutateToggleBoxBorder(box, side);
      });
    }
  });
};

export const onChangeColor =
  (boxId, updateBox, configuration) => (selectedColor) => {
    updateBox(boxId, (box) => {
      box.backgroundColor = {
        label: selectedColor,
        style: configuration.colors.find(
          (color) => color.label === selectedColor
        ).style,
      };
    });
  };

export const onChangeBorderColor =
  (boxId, updateBox, configuration) => (selectedColor) => {
    updateBox(boxId, (box) => {
      box.border.size = 3;
      box.border.color = {
        label: selectedColor,
        style: configuration.colors.find(
          (color) => color.label === selectedColor
        ).style,
      };
    });
  };

// Retire les styles qui vérifient `matches` de toute la sélection, et pas
// seulement ceux de son premier caractère (getCurrentInlineStyle) : sinon une
// taille posée sur une sélection commençant par un caractère sans taille
// s'ajoutait à l'ancienne, et l'ordre des deux, changé à l'enregistrement,
// donnait une autre taille dans le PDF que dans l'éditeur. Curseur sans
// sélection : styles de la prochaine saisie.
export const clearInlineStyles = (editorState, matches) => {
  const selection = editorState.getSelection();
  if (selection.isCollapsed()) {
    return editorState
      .getCurrentInlineStyle()
      .filter(matches)
      .reduce((state, style) => RichUtils.toggleInlineStyle(state, style), editorState);
  }
  const content = editorState.getCurrentContent();
  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  const found = new Set();
  let inRange = false;
  content.getBlockMap().forEach((block) => {
    const key = block.getKey();
    if (key === startKey) inRange = true;
    if (inRange) {
      const start = key === startKey ? selection.getStartOffset() : 0;
      const end = key === endKey ? selection.getEndOffset() : block.getLength();
      for (let offset = start; offset < end; offset += 1) {
        block.getInlineStyleAt(offset).filter(matches).forEach((style) => found.add(style));
      }
    }
    if (key === endKey) inRange = false;
  });
  if (!found.size) return editorState;
  const cleared = [...found].reduce(
    (nextContent, style) => Modifier.removeInlineStyle(nextContent, selection, style),
    content
  );
  return EditorState.forceSelection(
    EditorState.push(editorState, cleared, 'change-inline-style'),
    selection
  );
};

// Couleur du texte : une seule par caractère, noir et blanc compris (ils
// n'ont pas le préfixe color_ des autres couleurs de la palette). Reposer la
// couleur de palette déjà présente au début de la sélection la retire.
export const applyTextColor = ({ color, colors, editor }) => {
  const labels = new Set(colors.map(({ label }) => label));
  const isColor = (style) => labels.has(style) || style.startsWith('color_');
  const isBaseColor = color === 'white' || color === 'black';
  const togglesOff = !isBaseColor && editor.getCurrentInlineStyle().has(color);
  const cleared = clearInlineStyles(editor, isColor);
  return togglesOff ? cleared : RichUtils.toggleInlineStyle(cleared, color);
};

export const applyInlineStyle = ({ selectedProperty, prefix = '', editor }) => {
  let nextContentState = editor.getCurrentContent();
  let nextEditorState = EditorState.push(
    editor,
    nextContentState,
    'change-inline-style'
  );
  //unset current style depending prefix
  nextEditorState = clearInlineStyles(nextEditorState, (elem) => elem?.includes(prefix));
  // apply any way
  nextEditorState = RichUtils.toggleInlineStyle(
    nextEditorState,
    selectedProperty
  );
  return nextEditorState;
};

export const removeInlineStyle = ({ prefix = '', editor }) => {
  let nextContentState = editor.getCurrentContent();
  let nextEditorState = EditorState.push(
    editor,
    nextContentState,
    'change-inline-style'
  );
  //unset current style depending prefix
  nextEditorState = clearInlineStyles(nextEditorState, (elem) => elem?.includes(prefix));
  return nextEditorState;
};

export const toggleInlineStyle = ({
  selectedProperty,
  prefix = '',
  editor,
}) => {
  let nextContentState = editor.getCurrentContent();
  let nextEditorState = EditorState.push(
    editor,
    nextContentState,
    'change-inline-style'
  );
  const currentStyle = editor.getCurrentInlineStyle();
  //unset current style depending prefix
  nextEditorState = clearInlineStyles(nextEditorState, (elem) => elem?.includes(prefix));
  //toggle new style if not already present
  if (currentStyle.has(selectedProperty)) {
    return nextEditorState;
  }
  nextEditorState = RichUtils.toggleInlineStyle(
    nextEditorState,
    selectedProperty
  );
  return nextEditorState;
};

export const highlightUnknownVariables = (editorStateToHighlight) => {
  let modifiedEditorState = editorStateToHighlight;
  const previousSelectionState = editorStateToHighlight.getSelection();
  const text = modifiedEditorState.getCurrentContent().getPlainText();
  // check si y'a des variables
  const variables = text.match(/\*\*\[.*?\]\*\*/g);
  if (variables && variables.length > 0) {
    // reéupère les différents blocs contenu dans draft
    const blocks = modifiedEditorState.getCurrentContent().getBlockMap();
    blocks.forEach((b) => {
      let remainingBlockText = b.getText();
      let substractedCharacters = 0;
      // si variables dans le block
      const unknownVars = remainingBlockText.match(/\*\*\[.*?\]\*\*/g);
      if (unknownVars && unknownVars.length > 0) {
        unknownVars.forEach((uv) => {
          const selectionState = SelectionState.createEmpty(b.key);
          const regex = new RegExp(`\\*\\*\\[${uv.slice(3, -3)}\\]\\*\\*`);
          const m = remainingBlockText.match(regex);
          const selectionStateWithRange = selectionState
            .set('anchorOffset', m.index + substractedCharacters) // start of selection within block
            .set('focusOffset', m.index + m[0].length + substractedCharacters); // end of selection within block
          const modifiedEditorWithSelection = EditorState.forceSelection(
            modifiedEditorState,
            selectionStateWithRange
          );
          modifiedEditorState = applyInlineStyle({
            selectedProperty: `HIGHLIGHT_yellow`,
            prefix: 'HIGHLIGHT_',
            editor: modifiedEditorWithSelection,
          }); // HIGHLIGHT_
          modifiedEditorState = EditorState.forceSelection(
            modifiedEditorState,
            previousSelectionState
          );
          substractedCharacters += m.index + m[0].length;
          remainingBlockText = remainingBlockText.substring(
            m.index + m[0].length
          );
        });
      }
    });
  }
  return modifiedEditorState;
};

export const contentChanged = (currentEditor, newEditor) => {
  const currentContentState = currentEditor.getCurrentContent();
  const newContentState = newEditor.getCurrentContent();
  return currentContentState !== newContentState;
};

// `lineStyle` et `separator` viennent de l'occurrence insérée (data de
// l'entité Draft) : ils décident du nombre de lignes rendues pour une valeur
// multi-lignes (adresse) et de ce qui sépare les lignes regroupées.
export const translateVariable = ({
  variableKey,
  variables,
  isTemplate,
  lineStyle,
  separator,
}) => {
  let translated;
  if (variables[variableKey]) {
    if (isTemplate && variables[variableKey].isRecursive) {
      const modelName = variableKey.split('.')[0];
      const recursiveModelLabels = useStore.getState().recursiveModelLabels;
      const modelLabel = recursiveModelLabels?.[modelName] ?? i18next.t(`recursive_sections.model_labels.${modelName}`, { defaultValue: modelName });
      const modelPreposition = i18next.t(`recursive_sections.model_label_prepositions.${modelName}`, { defaultValue: `du ${modelLabel.toLowerCase()}` });
      translated = i18next.t('recursive_sections.field_label_pattern', {
        fieldLabel: variables[variableKey].name,
        modelLabel: modelLabel.toLowerCase(),
        modelPreposition,
        defaultValue: `${variables[variableKey].name} (${modelLabel})`,
      });
    } else {
      translated =
        variables[variableKey].value === ''
          ? variables[variableKey].name
          : variables[variableKey].value;
    }
  } else {
    return null;
  }
  if (typeof translated === 'number') {
    translated = translated.toString();
  }
  return applyVariableLineStyle(translated, lineStyle, separator);
};

export const getEntities = (editorState, entityType = null) => {
  const content = editorState.getCurrentContent();
  const entities = [];
  content.getBlocksAsArray().forEach((block) => {
    let selectedEntity = null;
    block.findEntityRanges(
      (character) => {
        if (character.getEntity() !== null) {
          const entity = content.getEntity(character.getEntity());
          if (!entityType || (entityType && entity.getType() === entityType)) {
            selectedEntity = {
              entityKey: character.getEntity(),
              blockKey: block.getKey(),
              entity: content.getEntity(character.getEntity()),
            };
            return true;
          }
        }
        return false;
      },
      (start, end) => {
        entities.push({ ...selectedEntity, start, end });
      }
    );
  });
  return entities;
};

export const convertContentToRawText = (editorState) => {
  const blocks = convertToRaw(editorState.getCurrentContent()).blocks;
  const mappedBlocks = blocks.map(
    (block) => (!block.text.trim() && '\n') || block.text
  );

  let newText = '';
  for (let i = 0; i < mappedBlocks.length; i++) {
    const block = mappedBlocks[i];

    // handle last block
    if (i === mappedBlocks.length - 1) {
      newText += block;
    } else {
      // otherwise we join with \n, except if the block is already a \n
      if (block === '\n') newText += block;
      else newText += block + '\n';
    }
  }
  return newText;
};

export const injectVariableInEditorState = ({
  editorState,
  variableKey,
  variablesInStore,
  isTemplate,
  startOffset,
  anchorOffset,
  blockKey,
}) => {
  let modifiedEditorState = editorState;

  const variableTranslated = translateVariable({
    variableKey,
    variables: variablesInStore,
    isTemplate,
  });
  if (variableTranslated === null) return editorState;

  const selectionState = SelectionState.createEmpty(blockKey).merge({
    anchorOffset,
    focusOffset: startOffset,
  });
  const contentstate = modifiedEditorState.getCurrentContent();
  let newContentState = contentstate.createEntity('VARIABLE', 'IMMUTABLE', {
    key: variableKey,
  });
  const entityKey = newContentState.getLastCreatedEntityKey();
  newContentState = Modifier.replaceText(
    newContentState,
    selectionState,
    variableTranslated,
    modifiedEditorState.getCurrentInlineStyle(anchorOffset),
    entityKey
  );
  modifiedEditorState = EditorState.push(
    modifiedEditorState,
    newContentState,
    'insert-characters'
  );
  modifiedEditorState = EditorState.forceSelection(
    modifiedEditorState,
    SelectionState.createEmpty(blockKey).merge({
      anchorOffset: anchorOffset + variableTranslated.length,
      focusOffset: anchorOffset + variableTranslated.length,
    })
  );
  return modifiedEditorState;
};

export const getBlockStyle = (block) => {
  switch (block.getType()) {
    case 'blockquote':
      return 'RichEditor-blockquote';
    case 'ordered-list-item':
      return 'RichEditor-ordered-list-item-no-marker';
    default:
      return null;
  }
};
// Coupe le contenu au curseur : ce qui précède reste, ce qui suit part dans une
// nouvelle box (touche Entrée). Une sélection non réduite à un point est
// d'abord supprimée, comme le ferait n'importe quelle saisie.
// Retourne deux ContentState ; `tail` est null quand le curseur est déjà à la
// fin (rien à déplacer, la nouvelle box naît vide).
export const splitContentAtSelection = (editorState) => {
  const selection = editorState.getSelection();
  const withoutSelection = selection.isCollapsed()
    ? editorState.getCurrentContent()
    : Modifier.removeRange(
        editorState.getCurrentContent(),
        selection,
        'backward'
      );
  const caret = selection.isCollapsed()
    ? selection
    : withoutSelection.getSelectionAfter();

  // splitBlock coupe le bloc courant en deux : la queue commence au bloc
  // désigné par la sélection résultante.
  const splitted = Modifier.splitBlock(withoutSelection, caret);
  const blocks = splitted.getBlockMap().toArray();
  const tailStart = blocks.findIndex(
    (block) => block.getKey() === splitted.getSelectionAfter().getStartKey()
  );

  const headBlocks = blocks.slice(0, tailStart);
  const tailBlocks = blocks.slice(tailStart);
  const entityMap = splitted.getEntityMap();

  const head = headBlocks.length
    ? ContentState.createFromBlockArray(headBlocks, entityMap)
    : ContentState.createFromText('');
  const tailIsEmpty =
    tailBlocks.length === 1 && tailBlocks[0].getText().length === 0;

  return {
    head,
    tail: tailIsEmpty
      ? null
      : ContentState.createFromBlockArray(tailBlocks, entityMap),
  };
};

// Box (image…) déplacée par react-dnd et lâchée sur un texte en édition : Draft
// insérerait le texte du dataTransfer, soit l'URL de l'image glissée. Les
// déplacements de texte internes et les dépôts natifs (texte venu d'ailleurs)
// gardent le comportement de Draft.
export const handleBoxDrop = (monitor) => (_selection, _data, dragType) => {
  if (dragType === 'internal' || !monitor?.isDragging()) return 'not-handled';
  return Object.values(NativeTypes).includes(monitor.getItemType()) ? 'not-handled' : 'handled';
};

export const handleBeforeInput = ({ chars, editorState, setEditorState }) => {
  if (chars === '. ') {
    const currentSelection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const contentBlock = contentState.getBlockForKey(
      currentSelection.getStartKey()
    );
    const start = currentSelection.getStartOffset();
    setEditorState(
      EditorState.set(editorState, {
        currentContent: Modifier.replaceText(
          editorState.getCurrentContent(),
          currentSelection,
          ' ',
          contentBlock.getInlineStyleAt(start - 1)
        ),
      })
    );
    return 'handled';
  }
  return 'not-handled';
};

const getEntityStrategy = (type) => {
  return function (contentBlock, callback, contentState) {
    contentBlock.findEntityRanges((character) => {
      const entityKey = character.getEntity();
      if (entityKey === null) {
        return false;
      }
      return contentState.getEntity(entityKey).type === type;
    }, callback);
  };
};

function findWithRegex(regex, contentBlock, callback) {
  const text = contentBlock.getText(); // Retrieve the text from the content block
  let matchArr, start;
  // Continue to execute the regex on the text until no more matches are found
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index; // Get the start index of the match
    callback(start, start + matchArr[0].length); // Call the provided callback with the match range
  }
}

const escapedFind = emojiToolkit.escapeRegExp(emojiToolkit.unicodeCharRegex());
// Ajouter les symboles spéciaux à la regex
const specialSymbols = [
  '▶',
  '►',
  '▲',
  '◆',
  '☀',
  '★',
  '⚡',
  '🎯',
  '💡',
  '🔧',
  '📝',
];
const escapedSpecialSymbols = specialSymbols
  .map((s) => emojiToolkit.escapeRegExp(s))
  .join('|');
const search = new RegExp(
  `<object[^>]*>.*?</object>|<span[^>]*>.*?</span>|<(?:object|embed|svg|img|div|span|p|a)[^>]*>|(${escapedFind})|(${escapedSpecialSymbols})`,
  'gi'
);

const emojiStrategy = (contentBlock, callback) => {
  findWithRegex(search, contentBlock, callback);
};

const buildDecorator = (emojiProps) => new CompositeDecorator([
  {
    strategy: getEntityStrategy('VARIABLE'),
    component: VariableDecorator,
  },
  {
    strategy: emojiStrategy,
    component: DraftEmoji,
    props: emojiProps,
  },
  {
    strategy: getEntityStrategy('IMAGE'),
    component: ImageDecorator,
  },
]);

export const decorator = buildDecorator();

// Un titre n'a pas de style inline FONT_SIZE_ : sa taille est celle de son
// niveau, posée sur la ligne du titre. Sans ce réglage, l'emoji prenait la
// taille du texte courant et paraissait minuscule à côté du titre.
export const titleDecorator = buildDecorator({ inheritFontSize: true });

// Fonction pour ajouter une image
export const insertImage = ({
  editorState,
  startOffset,
  anchorOffset,
  blockKey,
  box,
  menuAncestorType,
  menuAncestorIds,
}) => {
  const contentState = editorState.getCurrentContent();
  const contentStateWithEntity = contentState.createEntity(
    'IMAGE',
    'IMMUTABLE',
    { box, menuAncestorType, menuAncestorIds }
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

  // Insertion de l'image avec un espace après
  const selectionState = SelectionState.createEmpty(blockKey).merge({
    anchorOffset,
    focusOffset: startOffset,
  });
  const newContentState = Modifier.replaceText(
    contentState,
    selectionState,
    ' ',
    null,
    entityKey
  );
  const newContentStateWithSpace = Modifier.insertText(
    newContentState,
    newContentState.getSelectionAfter(),
    ' '
  );

  // Ajuster la sélection pour qu'elle soit après l'image et l'espace inséré
  const newEditorState = EditorState.push(
    editorState,
    newContentStateWithSpace,
    'insert-characters'
  );
  const newSelection = newContentStateWithSpace.getSelectionAfter();
  const editorStateWithNewSelection = EditorState.forceSelection(
    newEditorState,
    newSelection
  );
  return editorStateWithNewSelection;
};

export const getAnchorOffset = ({ editorState, startOffset, delimiter }) => {
  const blockKey = editorState.getSelection().getStartKey();
  const blockText = editorState
    .getCurrentContent()
    .getBlockForKey(blockKey)
    .getText();
  let anchorOffset;
  for (
    anchorOffset = startOffset;
    anchorOffset > 0 && blockText[anchorOffset] !== delimiter;
    anchorOffset -= 1
  );
  return anchorOffset;
};

export const getAncestorFromType = (type, parent) => {
  if (type === ItemTypes.CUSTOM_TEXT_VARIABLE)
    return MENU_ANCESTORS.CUSTOM_TEXT_VARIABLE[parent];
  if (type === ItemTypes.VARIABLE_GROUP)
    return MENU_ANCESTORS.VARIABLE_GROUP[parent];
  return MENU_ANCESTORS.BOX[parent];
};

export const replaceStyle = (editorState, oldStyle, newStyle) => {
  const contentState = editorState.getCurrentContent();
  const blockMap = contentState.getBlockMap();

  let newContentState = contentState;

  blockMap.forEach((block) => {
    const blockKey = block.getKey();
    const characterList = block.getCharacterList();

    // Variable pour marquer le début d'une plage de texte avec l'ancien style
    let start = null;

    // Parcourir chaque caractère du bloc
    characterList.forEach((char, index) => {
      if (char.hasStyle(oldStyle)) {
        // Si le caractère a l'ancien style et qu'aucune plage n'est en cours, démarrer une nouvelle plage
        if (start === null) {
          start = index;
        }
      } else {
        // Si le caractère n'a pas l'ancien style et qu'une plage est en cours, terminer la plage
        if (start !== null) {
          const selection = SelectionState.createEmpty(blockKey).merge({
            anchorOffset: start,
            focusOffset: index,
          });

          // Retirer l'ancien style et appliquer le nouveau style en une seule opération
          newContentState = Modifier.applyInlineStyle(
            Modifier.removeInlineStyle(newContentState, selection, oldStyle),
            selection,
            newStyle
          );
          start = null; // Réinitialiser le marqueur de plage
        }
      }
    });

    // Traiter la fin du bloc si une plage de style est encore ouverte
    if (start !== null) {
      const selection = SelectionState.createEmpty(blockKey).merge({
        anchorOffset: start,
        focusOffset: characterList.size,
      });

      // Retirer l'ancien style et appliquer le nouveau style pour la plage restante
      newContentState = Modifier.applyInlineStyle(
        Modifier.removeInlineStyle(newContentState, selection, oldStyle),
        selection,
        newStyle
      );
    }
  });

  return EditorState.push(editorState, newContentState, 'change-inline-style');
};

export const AddVariableInContentTextRichEditor = (editorState, content) => {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();

  const newContentState = Modifier.insertText(
    contentState,
    selectionState,
    content
  );

  const newEditorState = EditorState.push(
    editorState,
    newContentState,
    'insert-characters'
  );
  return newEditorState;
};
